import React, { useContext, useCallback } from 'react';
import {
  Field,
  mergeClasses,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import {
  DateRangePickerWithCompare,
  useDefaultPresetOptions,
  ComparisonBehavior,
  StartOfWeek,
} from '@bingads-webui-fluent-component/date-range-picker-with-compare';

export const useDateSelectorStyles = makeStyles({
  dateSelector: {
    display: 'grid',
  },
  label: {
    lineHeight: tokens.lineHeightBase200,
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    '& label': {
      fontWeight: tokens.fontWeightRegular,
    },
  },
});
interface DateSelectorProps {
  selectedStartDate: Date;
  selectedEndDate: Date;
  setSelectedStartDate: (date:Date) => void;
  setSelectedEndDate: (date:Date) => void;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  selectedStartDate, selectedEndDate, setSelectedStartDate, setSelectedEndDate,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const styles = useDateSelectorStyles();

  const onSelectDate = useCallback((dateRange: { endDate:Date, startDate:Date }) => {
    setSelectedEndDate(dateRange.endDate);
    setSelectedStartDate(dateRange.startDate);
  }, []);

  return (
    <div className={mergeClasses('date-selector', styles.dateSelector)}>
      <Field
        label={i18n?.getString(_TL_('Date range'))}
        size="small"
        className={styles.label}
      >
        <DateRangePickerWithCompare
          dateRangePickerPresetMenuProps={{
            presetDateRangeOptions: useDefaultPresetOptions(
              StartOfWeek.SUNDAY,
              {
                today: i18n?.getString(_TL_('Today')),
                last7Days: i18n?.getString(_TL_('Last 7 days')),
                last30Days: i18n?.getString(_TL_('Last 30 days')),
                thisMonth: i18n?.getString(_TL_('This month')),
                lastMonth: i18n?.getString(_TL_('Last month')),
                last14Days: i18n?.getString(_TL_('Last 14 days')),
                lastWeek: i18n?.getString(_TL_('Last week')),
                thisWeek: i18n?.getString(_TL_('This week')),
              },
            ),
            customDateRangeOption: {
              id: 'custom',
              name: i18n?.getString(_TL_('Custom')),
              isDefaultSelected: true,
              defaultCustomDateRangeSelection: {
                startDate: selectedStartDate,
                endDate: selectedEndDate,
              },
            },
          }}
          onApply={(dateRange: any, _preset: any, _cdateRange: any, _comparePreset: any) => {
            onSelectDate(dateRange);
          }}
          dateRangePickerComparisonProps={{
            comparisonBehavior: ComparisonBehavior.NONE,
            presetCompareOptions: [],
          }}
          localizedStrings={{
            comparisonSectionLabel: i18n?.getString(_TL_('Comparison period')),
            customDateRangeSelectionLabel: i18n?.getString(_TL_('Custom date range')),
            dateRangePickerApplyButtonLabel: i18n?.getString(_TL_('Apply')),
          }}
          dateRangePickerCalendarProps={
            {
              calendarProps: {
                strings: {
                  months: [i18n?.getString(_TL_('January')), i18n?.getString(_TL_('February')), i18n?.getString(_TL_('March')), i18n?.getString(_TL_('April')), i18n?.getString(_TL_('May')), i18n?.getString(_TL_('June')), i18n?.getString(_TL_('July')), i18n?.getString(_TL_('August')), i18n?.getString(_TL_('September')), i18n?.getString(_TL_('October')), i18n?.getString(_TL_('November')), i18n?.getString(_TL_('December'))],
                  shortMonths: [i18n?.getString(_TL_('Jan')), i18n?.getString(_TL_('Feb')), i18n?.getString(_TL_('Mar')), i18n?.getString(_TL_('Apr')), i18n?.getString(_TL_('May')), i18n?.getString(_TL_('Jun')), i18n?.getString(_TL_('Jul')), i18n?.getString(_TL_('Aug')), i18n?.getString(_TL_('Sep')), i18n?.getString(_TL_('Oct')), i18n?.getString(_TL_('Nov')), i18n?.getString(_TL_('Dec'))],
                  days: [i18n?.getString(_TL_('Sunday')), i18n?.getString(_TL_('Monday')), i18n?.getString(_TL_('Tuesday')), i18n?.getString(_TL_('Wednesday')), i18n?.getString(_TL_('Thursday')), i18n?.getString(_TL_('Friday')), i18n?.getString(_TL_('Saturday'))],
                  shortDays: [i18n?.getString(_TL_('S')), i18n?.getString(_TL_('M')), i18n?.getString(_TL_('T')), i18n?.getString(_TL_('W')), i18n?.getString(_TL_('T')), i18n?.getString(_TL_('F')), i18n?.getString(_TL_('S'))],
                  goToToday: i18n?.getString(_TL_('Go to today')),
                  prevMonthAriaLabel: i18n?.getString(_TL_('Go to previous month')),
                  nextMonthAriaLabel: i18n?.getString(_TL_('Go to next month')),
                  prevYearAriaLabel: i18n?.getString(_TL_('Go to previous year')),
                  nextYearAriaLabel: i18n?.getString(_TL_('Go to next year')),
                  closeButtonAriaLabel: i18n?.getString(_TL_('Close date picker')),
                  isRequiredErrorMessage: i18n?.getString(_TL_('Field is required')),
                  invalidInputErrorMessage: i18n?.getString(_TL_('Invalid date format.')),
                },
              },
            }
          }
        />
      </Field>
    </div>
  );
};
